<template>
    <tailwind-layout title="Agent Accounts">
        <template #header>
            Agent Accounts
        </template>

        <div class="bg-rent-risk-blue-100 overflow-hidden shadow-xl sm:rounded-lg p-6">
            <div class="relative">
                <div class="mb-6 flex justify-items-stretch">
                    <div class="w-4/12 sm:w-1/12 pr-4">
                        <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="w-8/12 sm:w-9/12 sm:pr-4">
                        <input id="search" v-model="form.search" type="text" placeholder="Search for Agent Accounts..." class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md">
                    </div>
                    <div class="sm:w-2/12 order-4 sm:order-4 flex items-start justify-end">
                        <button @click="newAccountsModal = true;" class="mt-1 sm:ml-3 primaryButton">
                            Add New Account(s)
                        </button>
                    </div>
                </div>
            </div>
            <div class="rounded-md shadow overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden">
                                <input ref="sort_by" type="hidden" v-model="form.sort_by" />
                                <input ref="sort_direction" type="hidden" v-model="form.sort_direction" />
                                <table class="min-w-full divide-y divide-gray-200">
                                    <thead class="bg-rent-risk-blue-400 text-white">
                                    <tr>
                                        <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                                            <span @click="sortTable('name')">
                                            Name
                                                <icon v-show="form.sort_by === 'name'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'name'" :name="'sort'" />
                                            </span>
                                        </th>
                                        <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                                            <span @click="sortTable('email')">
                                                Email
                                                <icon v-show="form.sort_by === 'email'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'email'" :name="'sort'" />
                                            </span>
                                        </th>
                                        <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                                            Account Activated
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>

                                    <tr v-for="(user, index) in users.data" :key="index" :class="index % 2 === 0 ? 'bg-gray-50 border-b hover:bg-yellow-50' : 'bg-white border-b hover:bg-yellow-50'">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 hover:text-indigo-900">
                                            {{ user.name }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <a :href="'mailto:'+user.email">
                                                {{ user.email }}
                                            </a>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {{ user.account_activated }}
                                        </td>
                                    </tr>
                                    <tr v-show="!users.data.length">
                                        <td colspan="5" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No Agent Accounts were found in the search.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="users.links" :results="users" :type="'users'" />
        </div>
        <div v-if="newAccountsModal">
            <TransitionRoot as="template" :show="newAccountsModal">
                <Dialog as="div" class="relative z-50" @close="newAccountsModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-end justify-center p-4 text-left sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-6 pb-6 pt-8 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                                    <div>
                                        <div class="mt-3 text-left sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Create Agent Account(s)</DialogTitle>
                                            <div v-if="Object.keys($page.props.errors).length" class="py-4 text-red-800 text-sm font-medium">
                                                <span v-if="Object.keys($page.props.errors).length === 1">There is one form error.</span>
                                                <span v-else>There are {{ Object.keys($page.props.errors).length }} form errors.</span>
                                            </div>
                                            <div class="mb-4 flex">
                                                <label for="manual" class="block text-sm font-medium text-gray-700"><input type="radio" v-model="userForm.type" value="manual"> Manual</label>
                                                <label for="csv" class="block text-sm font-medium text-gray-700 ml-5"><input type="radio" v-model="userForm.type" value="csv"> Upload CSV</label>
                                            </div>

                                            <div v-if="userForm.type === 'manual'" class="mt-4">
                                                <!-- Add form fields for First Name, Last Name, and Email -->
                                                <div class="mb-4">
                                                    <label for="first_name" class="block text-sm font-medium text-gray-700">First Name</label>
                                                    <input type="text" id="first_name" v-model="userForm.first_name" class="mt-1 p-3 w-full border rounded-md">
                                                    <p v-text="userForm.errors.first_name" class="text-red-500 text-sm"></p>
                                                </div>
                                                <div class="mb-4">
                                                    <label for="last_name" class="block text-sm font-medium text-gray-700">Last Name</label>
                                                    <input type="text" id="last_name" v-model="userForm.last_name" class="mt-1 p-3 w-full border rounded-md">
                                                    <p v-text="userForm.errors.last_name" class="text-red-500 text-sm"></p>
                                                </div>
                                                <div class="mb-4">
                                                    <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
                                                    <input type="email" id="email" v-model="userForm.email" class="mt-1 p-3 w-full border rounded-md">
                                                    <p v-text="userForm.errors.email" class="text-red-500 text-sm"></p>
                                                </div>
                                            </div>
                                            <div v-if="userForm.type === 'csv'" class="mt-4">
                                                <div class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                                                    <p class="font-bold">Note</p>
                                                    <p>First Row of CSV file must be a header row. The headings must be First Name, Last Name and Email</p>
                                                </div>
                                                <div class="mb-4 mt-4">
                                                    <label for="csv_file" class="block text-sm font-medium text-gray-700">CSV File</label>
                                                    <input type="file" @input="userForm.csv_file = $event.target.files[0]" id="csv_file" class="mt-1 p-3 w-full border rounded-md">
                                                    <p v-text="userForm.errors.csv_file" class="text-red-500 text-sm"></p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-4 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/3 mr-2 justify-center rounded-md bg-red-600 px-2 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600" @click="newAccountsModal = false">Close</button>
                                        <div class="flex-shrink-0 w-2/3 ml-2">
                                            <button type="button" @click="storeAgentAccount" :disabled="userForm.processing" class="w-full justify-center rounded-md bg-rent-risk-blue-400 px-2 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">{{userForm.type === 'manual' ? 'Create' : 'Upload'}}</button>
                                        </div>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>


        </div>
    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import Button from "@/Jetstream/Button.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from "@headlessui/vue";

export default {
    components: {
        DialogTitle,
        Dialog,
        TransitionChild,
        TransitionRoot,
        DialogPanel,
        FontAwesomeIcon,
        Button,
        Icon,
        Pagination,
        TailwindLayout,
    },
    props: {
        filters: Object,
        users: Object,
    },
    data() {
        return {
            faTrash,
            newAccountsModal: false,
            form: {
                search: this.filters.search,
                paginate: (this.filters.paginate)?this.filters.paginate:25,
                sort_by: this.filters.sort_by,
                sort_direction_icon : (this.filters.sort_direction === 'desc')?'sort-up':'sort-down'
            },
            userForm: this.$inertia.form({
                type: 'manual',
                csv_file: null,
                first_name: null,
                last_name: null,
                email: null,
            }),
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get(this.route('agent-accounts.index'), pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        sortTable(column) {
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc')?'asc':'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc')?'sort-up':'sort-down'
       },
        reset() {
            this.form = mapValues(this.form, () => null)
        },

        storeAgentAccount() {
            this.userForm.post(this.route("agent-accounts.store"), {
                onSuccess: () => {
                    this.newAccountsModal= false;
                    this.userForm.reset();
                },
            });
        }
    },
}
</script>
